import { useMemo, useState, type ReactNode } from "react";
import type { UniverseSectorGroup, UniverseTicker } from "../../shared/types";
import { ErrorBanner, Spinner } from "../components/ui";
import { api } from "../lib/api";
import { usePoll } from "../lib/format";

// On-palette dots so each sector reads at a glance.
const DOTS = [
  "bg-accent",
  "bg-gain",
  "bg-gold",
  "bg-clock",
  "bg-lossbright",
  "bg-silver",
  "bg-bronze",
  "bg-accent/60",
  "bg-gain/60",
  "bg-clock/60",
  "bg-gold/60",
];

export default function UniversePage() {
  // Poll so picks grey out here shortly after being locked. Ownership only
  // changes on a pick (~once/min at most), so a slow poll is plenty and keeps
  // request volume low.
  const { data, error, loading } = usePoll(api.universeAll, 30_000);
  const [q, setQ] = useState("");
  const [sector, setSector] = useState<string | null>(null);

  const query = q.trim().toLowerCase();

  const visible = useMemo(() => {
    if (!data) return [];
    return data.sectors
      .filter((g) => !sector || g.sector === sector)
      .map((g) => ({
        ...g,
        tickers: query
          ? g.tickers.filter(
              (t) =>
                t.ticker.toLowerCase().includes(query) ||
                (t.name ?? "").toLowerCase().includes(query),
            )
          : g.tickers,
      }))
      .filter((g) => g.tickers.length > 0);
  }, [data, sector, query]);

  const dotFor = (s: string) =>
    data ? DOTS[data.sectors.findIndex((g) => g.sector === s) % DOTS.length] : "bg-muted";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-fg">
            Draft <span className="text-brand">Pool</span>
          </h1>
          {data && (
            <p className="mt-1 text-sm text-muted">
              {data.total.toLocaleString()} stocks across {data.sectors.length}{" "}
              sectors · <span className="text-faint">{data.ownedCount} drafted</span>
            </p>
          )}
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter ticker or company…"
          className="num w-72 max-w-full rounded-lg border border-edge bg-ink-900 px-3 py-2 text-fg placeholder:text-faint focus:border-accent"
        />
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && !data && <Spinner label="Loading universe…" />}

      {data && (
        <>
          {/* Sector filter chips */}
          <div className="flex flex-wrap gap-2">
            <Chip active={sector === null} onClick={() => setSector(null)}>
              All <span className="text-faint">{data.total}</span>
            </Chip>
            {data.sectors.map((g) => (
              <Chip
                key={g.sector}
                active={sector === g.sector}
                onClick={() => setSector(sector === g.sector ? null : g.sector)}
              >
                <span className={`h-2 w-2 rounded-full ${dotFor(g.sector)}`} />
                {g.sector} <span className="text-faint">{g.count}</span>
              </Chip>
            ))}
          </div>

          {visible.length === 0 ? (
            <p className="py-10 text-center text-muted">No matches.</p>
          ) : (
            <div className="space-y-7">
              {visible.map((g) => (
                <SectorSection key={g.sector} group={g} dot={dotFor(g.sector)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Chip({
  children,
  active,
  onClick,
}: {
  children: ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
        active
          ? "border-accent/60 bg-accent/15 text-fg"
          : "border-edge text-muted hover:text-fg"
      }`}
    >
      {children}
    </button>
  );
}

function SectorSection({
  group,
  dot,
}: {
  group: UniverseSectorGroup;
  dot: string;
}) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2 border-b border-edge pb-2">
        <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
        <h2 className="text-sm font-bold uppercase tracking-widest text-fg">
          {group.sector}
        </h2>
        <span className="num text-xs text-faint">{group.count}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {group.tickers.map((t) => (
          <TickerCard key={t.ticker} t={t} />
        ))}
      </div>
    </section>
  );
}

function TickerCard({ t }: { t: UniverseTicker }) {
  const owned = t.ownedLong || t.ownedShort;
  return (
    <div
      className={`panel-tight flex flex-col justify-center gap-0.5 px-3 py-2 transition ${
        owned ? "opacity-40" : "hover:border-accent/40"
      }`}
      title={owned ? `${t.ticker} — drafted` : t.ticker}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="num text-sm font-bold text-fg">{t.ticker}</span>
        <span className="flex shrink-0 gap-1">
          {t.ownedLong && (
            <span className="rounded bg-gain/20 px-1 text-[10px] font-bold text-gain">L</span>
          )}
          {t.ownedShort && (
            <span className="rounded bg-loss/20 px-1 text-[10px] font-bold text-loss">S</span>
          )}
        </span>
      </div>
      <span className="truncate text-[11px] text-muted">{t.name}</span>
    </div>
  );
}
